import React, { useState } from 'react';
import {
  Search, RefreshCw, CheckCircle, XCircle, UserCheck, UserX, Trash2,
  ChevronLeft, ChevronRight, ShieldCheck, ShieldOff, MonitorPlay, UserPlus, Copy, X
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import axiosInstance from '../../api/api';

function AddMemberModal({ onClose, onCreated }) {
  const [form, setForm] = useState({
    name: '', email: '', user_type: 'worker', phone: '', location: '',
    company: '', password: '', send_welcome_email: true,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [created, setCreated] = useState(null);

  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));

  const submit = async () => {
    setError('');
    if (!form.name.trim() || !form.email.trim()) { setError('Name and email are required.'); return; }
    if (form.password && form.password.length < 6) { setError('Password must be at least 6 characters (or leave blank to auto-generate).'); return; }
    setSaving(true);
    try {
      const payload = { ...form, password: form.password || null };
      const r = await axiosInstance.post('/admin/users', payload);
      setCreated(r.data);
      onCreated();
    } catch (e) {
      const d = e.response?.data?.detail;
      setError(typeof d === 'string' ? d : 'Could not create member.');
    }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50" data-testid="add-member-modal">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-4 border-b">
          <h3 className="font-bold text-lg flex items-center gap-2"><UserPlus className="h-5 w-5 text-[#2B4C6F]" /> Add Member</h3>
          <button onClick={onClose} data-testid="add-member-close"><X className="h-5 w-5 text-gray-500 hover:text-black" /></button>
        </div>

        {created ? (
          <div className="p-5 space-y-3" data-testid="add-member-success">
            <div className="bg-emerald-50 border border-emerald-300 rounded-lg p-3 text-sm">
              <p className="font-bold text-emerald-800 mb-1">✓ Member created</p>
              <p>{created.user.name} ({created.user.user_type}) — {created.user.email}</p>
              {created.welcome_email_sent && <p className="text-emerald-700 mt-1">Welcome email with credentials sent.</p>}
            </div>
            <div className="bg-amber-50 border border-amber-300 rounded-lg p-3">
              <p className="text-xs font-bold uppercase text-amber-800 mb-1">
                {created.password_generated ? 'Generated temporary password' : 'Password set'}
              </p>
              <div className="flex items-center gap-2">
                <code className="flex-1 bg-white border rounded px-2 py-1.5 text-sm font-mono" data-testid="add-member-temp-password">{created.temp_password}</code>
                <Button size="sm" variant="outline" onClick={() => navigator.clipboard?.writeText(created.temp_password)} data-testid="add-member-copy-password">
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
              <p className="text-xs text-amber-700 mt-2">Share it securely — it won't be shown again.</p>
            </div>
            <Button className="w-full bg-[#2B4C6F] hover:bg-[#203A55]" onClick={onClose} data-testid="add-member-done">Done</Button>
          </div>
        ) : (
          <div className="p-5 space-y-3">
            {error && <div className="bg-red-50 border border-red-300 text-red-700 text-sm rounded-lg p-2.5" data-testid="add-member-error">{error}</div>}
            <Input placeholder="Full name *" value={form.name} onChange={set('name')} data-testid="add-member-name" />
            <Input placeholder="Email *" type="email" value={form.email} onChange={set('email')} data-testid="add-member-email" />
            <select value={form.user_type} onChange={set('user_type')} className="w-full px-3 py-2 border border-gray-300 rounded-md" data-testid="add-member-role">
              <option value="worker">Worker</option>
              <option value="contractor">Contractor</option>
              <option value="homeowner">Homeowner</option>
            </select>
            <div className="grid grid-cols-2 gap-2">
              <Input placeholder="Phone" value={form.phone} onChange={set('phone')} data-testid="add-member-phone" />
              <Input placeholder="Location (City, ST)" value={form.location} onChange={set('location')} data-testid="add-member-location" />
            </div>
            {form.user_type === 'contractor' && (
              <Input placeholder="Company name" value={form.company} onChange={set('company')} data-testid="add-member-company" />
            )}
            <Input placeholder="Password (leave blank to auto-generate)" type="text" value={form.password} onChange={set('password')} data-testid="add-member-password" />
            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input type="checkbox" checked={form.send_welcome_email} onChange={set('send_welcome_email')} data-testid="add-member-send-email" />
              Email the member their login details
            </label>
            <Button className="w-full bg-[#2B4C6F] hover:bg-[#203A55]" onClick={submit} disabled={saving} data-testid="add-member-submit">
              {saving ? 'Creating…' : 'Create Member'}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

export default function UsersTab({
  users, searchQuery, setSearchQuery, userTypeFilter, setUserTypeFilter,
  fetchUsers, formatDate, actionLoading,
  handleVerifyUser, handleSuspendUser, handleDeleteUser, handleToggleAdmin, handleImpersonate,
  usersPage, usersTotalPages, setUsersPage
}) {
  const [showAddMember, setShowAddMember] = useState(false);
  return (
    <div className="space-y-4" data-testid="users-tab">
      {showAddMember && <AddMemberModal onClose={() => setShowAddMember(false)} onCreated={fetchUsers} />}
      <div className="bg-white rounded-xl shadow-md p-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              placeholder="Search users..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
              data-testid="search-users"
            />
          </div>
          <select
            value={userTypeFilter}
            onChange={(e) => setUserTypeFilter(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md"
            data-testid="filter-user-type"
          >
            <option value="all">All Types</option>
            <option value="worker">Workers</option>
            <option value="contractor">Contractors</option>
            <option value="homeowner">Homeowners</option>
            <option value="admin">Admins</option>
          </select>
          <Button onClick={fetchUsers} variant="outline">
            <RefreshCw className="h-4 w-4" />
          </Button>
          <Button onClick={() => setShowAddMember(true)} className="bg-[#2B4C6F] hover:bg-[#203A55] text-white" data-testid="add-member-btn">
            <UserPlus className="h-4 w-4 mr-2" /> Add Member
          </Button>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-md overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">User</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Joined</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {users.map((user) => (
              <tr key={user._id} className="hover:bg-gray-50" data-testid={`user-row-${user._id}`}>
                <td className="px-6 py-4">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                      <span className="text-blue-600 font-semibold">{user.name?.charAt(0).toUpperCase()}</span>
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">{user.name}</p>
                      <p className="text-sm text-gray-500">{user.email}</p>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4">
                  <Badge className={
                    user.user_type === 'admin' ? 'bg-red-100 text-red-700' :
                    user.user_type === 'contractor' ? 'bg-green-100 text-green-700' :
                    user.user_type === 'homeowner' ? 'bg-amber-100 text-amber-700' :
                    'bg-blue-100 text-blue-700'
                  }>{user.user_type}</Badge>
                </td>
                <td className="px-6 py-4">
                  <div className="flex items-center space-x-2">
                    {user.suspended ? (
                      <Badge className="bg-red-100 text-red-700">Suspended</Badge>
                    ) : (
                      <Badge className="bg-green-100 text-green-700">Active</Badge>
                    )}
                    {user.verified && <CheckCircle className="h-4 w-4 text-blue-500" title="Verified" />}
                  </div>
                </td>
                <td className="px-6 py-4 text-sm text-gray-500">{formatDate(user.created_at)}</td>
                <td className="px-6 py-4">
                  <div className="flex justify-end space-x-2">
                    {user.user_type !== 'admin' ? (
                      <>
                        <Button size="sm" variant="outline" onClick={() => handleImpersonate(user._id, user.name || user.email)} disabled={actionLoading === user._id} className="text-blue-600 hover:bg-blue-50" title="Test dashboard as this user" data-testid={`impersonate-${user._id}`}>
                          <MonitorPlay className="h-4 w-4" />
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => handleVerifyUser(user._id, user.verified)} disabled={actionLoading === user._id} title={user.verified ? 'Remove verification' : 'Verify user'}>
                          {user.verified ? <XCircle className="h-4 w-4" /> : <UserCheck className="h-4 w-4" />}
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => handleSuspendUser(user._id, user.suspended)} disabled={actionLoading === user._id} className={user.suspended ? 'text-green-600' : 'text-yellow-600'} title={user.suspended ? 'Unsuspend user' : 'Suspend user'}>
                          {user.suspended ? <UserCheck className="h-4 w-4" /> : <UserX className="h-4 w-4" />}
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => handleToggleAdmin(user._id, false, user.name || user.email)} disabled={actionLoading === user._id} className="text-purple-600 hover:bg-purple-50" title="Promote to admin" data-testid={`promote-admin-${user._id}`}>
                          <ShieldCheck className="h-4 w-4" />
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => handleDeleteUser(user._id)} disabled={actionLoading === user._id} className="text-red-600 hover:bg-red-50" title="Delete user">
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </>
                    ) : (
                      <Button size="sm" variant="outline" onClick={() => handleToggleAdmin(user._id, true, user.name || user.email)} disabled={actionLoading === user._id} className="text-red-600 hover:bg-red-50" title="Demote from admin" data-testid={`demote-admin-${user._id}`}>
                        <ShieldOff className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
          <p className="text-sm text-gray-500">Page {usersPage} of {usersTotalPages}</p>
          <div className="flex space-x-2">
            <Button size="sm" variant="outline" disabled={usersPage === 1} onClick={() => setUsersPage(p => p - 1)}>
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button size="sm" variant="outline" disabled={usersPage >= usersTotalPages} onClick={() => setUsersPage(p => p + 1)}>
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
